// Въпросите на листа „Изписване" — премахване на ред, разделяне на рецепта на
// продукти и връщане в рецепта. Чисти функции (без React state): прегледът
// показва ТОЧНО редовете, които ще се появят/съберат, затова разделянето строи
// и прегледа, и самите редове от един и същ план (`splitRecipePlan`).
import type { TFunction } from "i18next";
import { ApartmentOutlined, DeleteOutlined, MergeCellsOutlined } from "@ant-design/icons";
import type { Product, RecipeIngredient } from "../../../../api/inventory";
import { clampDecimals } from "../../components/qty";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import type { ConfirmNote, ConfirmOptions } from "../../components/confirm/confirmTypes";
import { formatPortionsInput, parseDecimal, type WriteoffLineDraft } from "./writeoffWorksheetShared";

// „2 порции" / „1 порция" / „1,5 порции"
export const portionsLabel = (t: TFunction, portions: number) =>
  t("inventoryPage.cfmPortions", { count: portions, value: formatPortionsInput(portions) });

// Един продуктов ред от разделянето: количество = съставка × порции
// (null — съставка без количество в рецептата; редът остава празен)
export type SplitPlanLine = {
  productId: number;
  productName: string;
  unit: string | null;
  decimals: number;
  quantity: number | null;
};

export const splitRecipePlan = (
  ingredients: RecipeIngredient[],
  portions: number,
  productById: Map<number, Product>,
): SplitPlanLine[] =>
  ingredients.map((ingredient) => {
    const product = productById.get(ingredient.productId);
    return {
      productId: ingredient.productId,
      productName: ingredient.productName || product?.name || `#${ingredient.productId}`,
      unit: ingredient.unit ?? product?.unit ?? null,
      decimals: clampDecimals(product?.decimalPlaces),
      quantity: ingredient.quantity ? ingredient.quantity * portions : null,
    };
  });

// Порциите на реда-рецепта; празно/нула → 1 (както е било и досега)
export const splitPortionsOf = (row: WriteoffLineDraft) => {
  const parsed = parseDecimal(row.quantity);
  return { portions: parsed > 0 ? parsed : 1, defaulted: !(parsed > 0) };
};

type SplitConfirmInput = {
  recipeName: string;
  portions: number;
  portionsDefaulted: boolean;
  plan: SplitPlanLine[];
};

export const splitRecipeConfirm = (
  t: TFunction,
  { recipeName, portions, portionsDefaulted, plan }: SplitConfirmInput,
): ConfirmOptions => {
  const count = plan.length;
  const notes: ConfirmNote[] = [
    {
      kind: "undo",
      text: t("inventoryPage.cfmSplitRecipeUndo", { action: t("inventoryPage.revertToRecipe") }),
    },
  ];
  if (portionsDefaulted)
    notes.unshift({ kind: "info", text: t("inventoryPage.cfmSplitRecipeDefaultPortions") });
  return {
    intent: "info",
    icon: <ApartmentOutlined />,
    title: t("inventoryPage.cfmSplitRecipeTitle", { name: recipeName }),
    explanation: t("inventoryPage.cfmSplitRecipeText", {
      count,
      portions: portionsLabel(t, portions),
    }),
    preview: {
      caption: t("inventoryPage.cfmSplitRecipeCaption"),
      items: plan.map((line, index) => ({
        key: `${line.productId}-${index}`,
        label: line.productName,
        value: line.quantity == null ? "—" : formatQtyUnit(line.quantity, line.decimals, line.unit),
        tone: line.quantity == null ? "muted" : "default",
      })),
      limit: 8,
    },
    note: notes,
    confirmLabel: t("inventoryPage.cfmSplitRecipeConfirm", { count }),
  };
};

// Името на реда за въпроса: продуктът или рецептата
const lineNameOf = (line: WriteoffLineDraft, productById: Map<number, Product>) =>
  line.kind === "recipe"
    ? line.recipeName
    : line.productId != null
      ? (productById.get(line.productId)?.name ?? `#${line.productId}`)
      : null;

// „2,000 кг" / „2 порции" — количеството, както е написано в реда
const lineQtyOf = (t: TFunction, line: WriteoffLineDraft, productById: Map<number, Product>) => {
  const quantity = parseDecimal(line.quantity);
  if (!(quantity > 0)) return null;
  if (line.kind === "recipe") return portionsLabel(t, quantity);
  const product = line.productId != null ? productById.get(line.productId) : undefined;
  return formatQtyUnit(quantity, product?.decimalPlaces, product?.unit);
};

export const removeRowConfirm = (
  t: TFunction,
  line: WriteoffLineDraft,
  productById: Map<number, Product>,
): ConfirmOptions => {
  const name = lineNameOf(line, productById);
  const qty = lineQtyOf(t, line, productById);
  return {
    intent: "danger",
    icon: <DeleteOutlined />,
    title: name
      ? t("inventoryPage.cfmRemoveRowTitle", { name })
      : t("inventoryPage.cfmRemoveRowTitlePlain"),
    explanation: qty
      ? t("inventoryPage.cfmRemoveRowText", { qty })
      : t("inventoryPage.cfmRemoveRowTextEmpty"),
    note: { kind: "undo", text: t("inventoryPage.cfmRemoveRowUndo") },
    confirmLabel: t("inventoryPage.cfmRemoveRowConfirm"),
  };
};

export const revertGroupConfirm = (
  t: TFunction,
  groupLines: WriteoffLineDraft[],
  productById: Map<number, Product>,
): ConfirmOptions => {
  const first = groupLines[0];
  const recipeName = first?.groupRecipeName ?? "";
  const portions = portionsLabel(t, first?.groupPortions ?? 1);
  return {
    intent: "warning",
    icon: <MergeCellsOutlined />,
    title: t("inventoryPage.cfmRevertTitle", { name: recipeName }),
    explanation: t("inventoryPage.cfmRevertText", {
      count: groupLines.length,
      name: recipeName,
      portions,
    }),
    preview: {
      caption: t("inventoryPage.cfmRevertCaption"),
      items: groupLines.map((line) => ({
        key: line.key,
        label: lineNameOf(line, productById) ?? "—",
        value: lineQtyOf(t, line, productById) ?? "—",
      })),
      limit: 8,
    },
    note: {
      kind: "undo",
      text: t("inventoryPage.cfmRevertUndo", { action: t("inventoryPage.splitToProducts") }),
    },
    confirmLabel: t("inventoryPage.cfmRevertConfirm"),
  };
};
